import {
	computeItemTaxOnExclusiveTaxable,
	getBillDiscountRate,
	getItemLineAfterBillDiscount,
	toDecimal,
	toMoneyNumber,
} from "../../../../utils/financeEngine";
import {
	buildTreatmentAdditionalServices,
	buildTreatmentInvestigationItems,
	isTreatmentPackageInvoice,
} from "../../../PatientAccounts/utils/patientAccountUtils";

const SYMBOL_TO_CURRENCY = {
	$: "USD",
	"₹": "INR",
	"€": "EUR",
	"£": "GBP",
};

export const resolveActivePrescription = (...candidates) => {
	for (const item of candidates) {
		if (item && typeof item === "object" && !Array.isArray(item) && item._id) {
			return item;
		}
	}
	return null;
};

export const normalizeCurrencyCode = (currency, fallback = "INR", allowedCurrencies = []) => {
	if (!currency && currency !== 0) return fallback;
	const trimmed = String(currency).trim();
	if (!trimmed) return fallback;
	if (SYMBOL_TO_CURRENCY[trimmed]) return SYMBOL_TO_CURRENCY[trimmed];
	const upper = trimmed.toUpperCase();
	if (upper.length === 3 && /^[A-Z]{3}$/.test(upper)) return upper;
	if (allowedCurrencies.includes(trimmed)) return trimmed;
	return fallback;
};

export const formatMasterOptionLabel = (
	name,
	amount,
	currency,
	getCurrencySymbol,
	fallbackCurrency = "INR",
	allowedCurrencies = []
) => {
	const code = normalizeCurrencyCode(currency, fallbackCurrency, allowedCurrencies);
	const symbol = getCurrencySymbol ? getCurrencySymbol(code) : code;
	const price = toMoneyNumber(amount);
	return `${name} (${symbol}${price.toFixed(2)})`;
};

export const formatItemDisplayLabel = (
	name,
	amount,
	currency,
	getCurrencySymbol,
	fallbackCurrency = "INR",
	allowedCurrencies = []
) => formatMasterOptionLabel(name, amount, currency, getCurrencySymbol, fallbackCurrency, allowedCurrencies);

export const convertLineAmount = (
	amount,
	fromCurrency,
	toCurrency,
	convertCurrency,
	fallbackCurrency = "INR",
	allowedCurrencies = []
) => {
	const from = normalizeCurrencyCode(fromCurrency, fallbackCurrency, allowedCurrencies);
	const to = normalizeCurrencyCode(toCurrency, fallbackCurrency, allowedCurrencies);
	if (from === to) return toMoneyNumber(amount);
	if (typeof convertCurrency !== "function") return toMoneyNumber(amount);
	return toMoneyNumber(convertCurrency(amount, from, to));
};

export const convertBillingAmount = (
	amount,
	fromCurrency,
	toCurrency,
	convertCurrency,
	fallbackCurrency = "INR",
	allowedCurrencies = []
) => {
	if (!amount || fromCurrency === toCurrency) return toMoneyNumber(amount);
	return convertLineAmount(
		amount,
		fromCurrency,
		toCurrency,
		convertCurrency,
		fallbackCurrency,
		allowedCurrencies
	);
};

export const sumConvertedLineAmounts = (
	items = [],
	targetCurrency,
	convertCurrency,
	fallbackCurrency = "INR",
	allowedCurrencies = []
) =>
	toMoneyNumber(
		items.reduce(
			(sum, item) =>
				sum +
				convertLineAmount(
					item.amount,
					item.currency,
					targetCurrency,
					convertCurrency,
					fallbackCurrency,
					allowedCurrencies
				),
			0
		)
	);

export const getBillingBaseCurrency = (prescription, fallbackCurrency = "INR") =>
	normalizeCurrencyCode(prescription?.consultation?.currency, fallbackCurrency);

/** Cumulative and current payment amounts on billing payloads (add-sales / pay-remaining). */
export const withBillingPaymentAmountFields = (
	billingPayload,
	{ existingPaidAmount = 0, currentPaidAmount = 0 } = {}
) => {
	const existing = toMoneyNumber(existingPaidAmount);
	const current = toMoneyNumber(currentPaidAmount);
	return {
		...billingPayload,
		totalPaidTillNow: toMoneyNumber(existing + current),
		currentPaidAmount: current,
	};
};

/** Prior appointment / package payments already collected before POS billing. */
export const getPriorBillingPaymentOffsets = ({
	prescription,
	grandTotal = 0,
	isTreatmentPackage = false,
	treatmentDetails = null,
} = {}) => {
	const grandTotalBase = toMoneyNumber(grandTotal);
	const invoice = prescription?.appointment_invoice || {};

	let paidSoFarBase = 0;
	if (isTreatmentPackage) {
		paidSoFarBase = toMoneyNumber(
			treatmentDetails?.totalCollected ?? invoice.paid_amount ?? 0
		);
	} else {
		paidSoFarBase = toMoneyNumber(invoice.paid_amount ?? 0);
	}

	const balanceDueBase = Math.max(0, toMoneyNumber(grandTotalBase - paidSoFarBase));

	return {
		paidSoFarBase,
		balanceDueBase,
		hasPriorPayment: paidSoFarBase > 0,
	};
};

const getInvestigationMasterId = (item) =>
	item?.investigation_id || item?._id || item?.id || item?.test_id;

export const parseLabMasterResponse = (response) => {
	const data = response?.data?.data;
	if (Array.isArray(data?.investigations)) return flattenLabMasterList(data.investigations);
	if (Array.isArray(data)) return flattenLabMasterList(data);
	return [];
};

export const flattenLabMasterList = (masterList = []) => {
	if (!Array.isArray(masterList)) return [];

	return masterList.flatMap((entry) => {
		if (Array.isArray(entry?.examinations) && entry.examinations.length > 0) {
			return entry.examinations.map((exam) => ({
				...entry,
				...exam,
				investigation_id: getInvestigationMasterId(exam) || getInvestigationMasterId(entry),
				test_name: exam.test_name || exam.name || entry.test_name || entry.name,
				name: exam.name || exam.test_name || entry.name || entry.test_name,
				price: exam.price ?? exam.charges ?? exam.amount ?? entry.price ?? entry.charges,
				currency: exam.currency || entry.currency,
				type: entry.type || exam.type || "laboratory",
			}));
		}
		return [entry];
	});
};

const findInvestigationMaster = (item, masterMap, masterList = []) => {
	const candidateIds = [item?.test_id, item?.investigation_id, item?.id, item?.service_id]
		.filter(Boolean)
		.map(String);

	for (const id of candidateIds) {
		const hit = masterMap.get(id);
		if (hit) return hit;
	}

	const targetName = String(item?.test_name || item?.name || "")
		.trim()
		.toLowerCase();
	if (!targetName) return null;

	return (
		masterList.find((master) => {
			const masterName = String(master?.test_name || master?.name || "")
				.trim()
				.toLowerCase();
			return masterName && masterName === targetName;
		}) || null
	);
};

const resolveInvestigationUnitPrice = (item, quantity, master) => {
	const fromUnit =
		item.unit_price !== undefined && item.unit_price !== null && item.unit_price !== ""
			? toMoneyNumber(item.unit_price)
			: 0;
	const fromPrice =
		item.price !== undefined && item.price !== null && item.price !== ""
			? toMoneyNumber(item.price)
			: 0;
	const fromAmount = item.amount > 0 ? toMoneyNumber(item.amount / quantity) : 0;
	const fromMaster = toMoneyNumber(
		master?.price ?? master?.charges ?? master?.amount ?? 0
	);

	if (fromUnit > 0) return { unitPrice: fromUnit, fromItem: true };
	if (fromPrice > 0) return { unitPrice: fromPrice, fromItem: true };
	if (fromAmount > 0) return { unitPrice: fromAmount, fromItem: true };
	return { unitPrice: fromMaster, fromItem: false };
};

const resolveServiceUnitPrice = (item, quantity, master) => {
	const fromUnit =
		item.unit_price !== undefined && item.unit_price !== null && item.unit_price !== ""
			? toMoneyNumber(item.unit_price)
			: 0;
	const fromPrice =
		item.price !== undefined && item.price !== null && item.price !== ""
			? toMoneyNumber(item.price)
			: 0;
	const fromAmount = item.amount > 0 ? toMoneyNumber(item.amount / quantity) : 0;
	const fromMaster = toMoneyNumber(
		master?.price ?? master?.charges ?? master?.amount ?? 0
	);

	if (fromUnit > 0) return { unitPrice: fromUnit, fromItem: true };
	if (fromPrice > 0) return { unitPrice: fromPrice, fromItem: true };
	if (fromAmount > 0) return { unitPrice: fromAmount, fromItem: true };
	return { unitPrice: fromMaster, fromItem: false };
};

export const parseGeneralServiceMasterResponse = (response) => {
	const data = response?.data?.data;
	if (Array.isArray(data?.general_services)) return data.general_services;
	if (Array.isArray(data)) return data;
	return [];
};

export const getConsultationDisplayName = (consultation = {}) => {
	if (consultation?.package_name) {
		return consultation.package_name;
	}
	return "Face To Face Consultation";
};

export const getConsultationAmount = (consultation = {}) => {
	const packagePrice = consultation?.package_price;
	if (packagePrice !== null && packagePrice !== undefined && packagePrice !== "") {
		return toMoneyNumber(packagePrice);
	}
	return toMoneyNumber(consultation?.consultation_charge || 0);
};

export const buildLabMasterMap = (masterList = []) => {
	const map = new Map();
	masterList.forEach((item) => {
		const id = getInvestigationMasterId(item);
		if (id) map.set(String(id), item);
	});
	return map;
};

export const buildGeneralServiceMasterMap = (masterList = []) => {
	const map = new Map();
	masterList.forEach((item) => {
		const id = item?.service_id || item?._id || item?.id;
		if (id) map.set(String(id), item);
	});
	return map;
};

export const extractPrescribedInvestigations = (
	investigation = [],
	fallbackCurrency = "INR",
	allowedCurrencies = []
) => {
	if (!Array.isArray(investigation)) return [];

	return investigation.flatMap((group) => {
		const examinations = group?.examinations || [];
		return examinations
			.filter((exam) => exam?.selected !== false)
			.map((exam) => {
				const unitPrice = toMoneyNumber(exam.price ?? exam.charges ?? exam.amount ?? 0);
				const quantity = Math.max(1, Number(exam.quantity) || 1);
				return {
					id: exam._id,
					test_id: exam._id,
					test_name: exam.name,
					name: exam.name,
					quantity,
					unit_price: unitPrice,
					amount: toMoneyNumber(unitPrice * quantity),
					currency: normalizeCurrencyCode(
						exam.currency || group?.currency || fallbackCurrency,
						fallbackCurrency,
						allowedCurrencies
					),
					type: group?.type || "laboratory",
					source: "prescribed",
					isPrescribed: true,
					isNewRow: false,
				};
			});
	});
};

/**
 * EMR `investigation[]` laboratory exams are doctor suggestions only.
 * They must not be auto-added to the Collect Payment lab table.
 */
export const extractSuggestedInvestigations = (investigation = []) => {
	if (!Array.isArray(investigation)) return [];

	return investigation.flatMap((group, groupIndex) => {
		const groupType = String(group?.type || "laboratory").toLowerCase();
		if (groupType && groupType !== "laboratory" && groupType !== "lab") {
			return [];
		}

		return (group?.examinations || [])
			.filter((exam) => exam?.selected !== false)
			.map((exam, examIndex) => {
				const id =
					exam?._id ||
					exam?.investigation_id ||
					`suggested-investigation-${groupIndex}-${examIndex}`;
				const name = exam?.name || exam?.test_name || "-";
				return {
					id,
					test_id: exam?._id || exam?.investigation_id || id,
					test_name: name,
					name,
					type: group?.type || "laboratory",
					suggestion_reason: group?.suggestion_reason || group?.test_reason || "",
					source: "suggested",
					isSuggested: true,
				};
			});
	});
};

export const enrichInvestigationsWithMaster = (
	items = [],
	masterList = [],
	fallbackCurrency = "INR",
	allowedCurrencies = []
) => {
	const flattenedMaster = flattenLabMasterList(masterList);
	const masterMap = buildLabMasterMap(flattenedMaster);
	return items.map((item) => {
		const master = findInvestigationMaster(item, masterMap, flattenedMaster);
		const quantity = Math.max(1, Number(item.quantity) || 1);
		const { unitPrice, fromItem } = resolveInvestigationUnitPrice(item, quantity, master);
		const currency = normalizeCurrencyCode(
			fromItem ? item.currency : master?.currency || item.currency,
			fallbackCurrency,
			allowedCurrencies
		);
		const amount = toMoneyNumber(unitPrice * quantity);
		const priceResolved = fromItem || (Boolean(master) && unitPrice > 0);

		return {
			...item,
			test_id: item.test_id || master?.investigation_id || master?._id || item.id,
			test_name: item.test_name || master?.test_name || master?.name || item.name,
			quantity,
			unit_price: unitPrice,
			amount,
			currency,
			type: item.type || master?.type || "laboratory",
			priceResolved,
		};
	});
};

const mapAppointmentInvoiceServiceToItem = (
	service = {},
	index = 0,
	fallbackCurrency = "INR",
	baseCurrency = "INR"
) => {
	const serviceType = String(service.service_type || "").toLowerCase();
	const isConsultation = serviceType === "consultation";
	const unitPrice = toMoneyNumber(service.price ?? service.charges ?? 0);
	const quantity = Math.max(1, Number(service.quantity) || 1);

	return {
		id: service._id || service.service_id || `invoice-service-${index}`,
		service_id: service.service_id || service._id || null,
		service_name:
			service.service_name ||
			service.name ||
			(isConsultation ? getConsultationDisplayName() : "Service"),
		name: service.service_name || service.name,
		quantity,
		unit_price: unitPrice,
		amount: toMoneyNumber(unitPrice * quantity),
		currency: normalizeCurrencyCode(service.currency || baseCurrency, fallbackCurrency),
		status: service.status || "pending",
		isConsultation,
		isPrescribed: true,
		isNewRow: false,
		source: "prescribed",
	};
};

export const buildServiceItemsFromAppointmentInvoice = (
	appointmentInvoice = {},
	fallbackCurrency = "INR"
) => {
	const services = appointmentInvoice?.services || [];
	if (!Array.isArray(services) || services.length === 0) return [];

	const baseCurrency = normalizeCurrencyCode(
		appointmentInvoice?.currency || fallbackCurrency,
		fallbackCurrency
	);

	return services
		.filter((service) => {
			const serviceType = String(service.service_type || "").toLowerCase();
			return (
				serviceType !== "laboratory" &&
				serviceType !== "pharmacy" &&
				serviceType !== "treatment_package"
			);
		})
		.map((service, index) =>
			mapAppointmentInvoiceServiceToItem(service, index, fallbackCurrency, baseCurrency)
		);
};

export const extractInvestigationsFromAppointmentInvoice = (
	appointmentInvoice = {},
	fallbackCurrency = "INR",
	allowedCurrencies = []
) => {
	const services = appointmentInvoice?.services || [];
	if (!Array.isArray(services) || services.length === 0) return [];

	const baseCurrency = normalizeCurrencyCode(
		appointmentInvoice?.currency || fallbackCurrency,
		fallbackCurrency
	);

	return services
		.filter((service) => String(service.service_type || "").toLowerCase() === "laboratory")
		.map((service, index) => {
			const unitPrice = toMoneyNumber(service.price ?? service.charges ?? 0);
			const quantity = Math.max(1, Number(service.quantity) || 1);
			return {
				id: service._id || service.service_id || `invoice-investigation-${index}`,
				test_id: service.service_id || service._id,
				test_name: service.service_name || service.name || "Investigation",
				name: service.service_name || service.name || "Investigation",
				quantity,
				unit_price: unitPrice,
				amount: toMoneyNumber(unitPrice * quantity),
				currency: normalizeCurrencyCode(
					service.currency || baseCurrency,
					fallbackCurrency,
					allowedCurrencies
				),
				type: service.service_type || "laboratory",
				source: "prescribed",
				isPrescribed: true,
				isNewRow: false,
			};
		});
};

export const buildInitialServiceItems = (prescription = {}, fallbackCurrency = "INR") => {
	if (!resolveActivePrescription(prescription)) return [];

	const appointmentInvoice = prescription?.appointment_invoice || {};
	const invoiceServiceItems = buildServiceItemsFromAppointmentInvoice(
		appointmentInvoice,
		fallbackCurrency
	);
	if (invoiceServiceItems.length > 0) {
		return invoiceServiceItems;
	}

	const items = [];
	const consultation = prescription?.consultation || {};
	const consultationAmount = getConsultationAmount(consultation);
	const consultationCurrency = normalizeCurrencyCode(
		consultation?.currency,
		fallbackCurrency
	);

	if (consultationAmount > 0 || consultation?.package_name) {
		const unitPrice = consultationAmount;
		const quantity = Math.max(1, Number(consultation?.quantity) || 1);
		items.push({
			id: `consultation-${prescription._id}`,
			service_id: null,
			service_name: getConsultationDisplayName(consultation),
			quantity,
			unit_price: unitPrice,
			amount: toMoneyNumber(unitPrice * quantity),
			currency: consultationCurrency,
			isConsultation: true,
			isPrescribed: true,
			isNewRow: false,
			source: "prescribed",
		});
	}

	const additionalServices = consultation?.additional_services || [];
	if (Array.isArray(additionalServices)) {
		additionalServices.forEach((service) => {
			const serviceType = String(service.service_type || "").toLowerCase();
			if (serviceType === "laboratory" || serviceType === "pharmacy") return;

			const unitPrice = toMoneyNumber(service.price ?? service.charges ?? 0);
			const quantity = Math.max(1, Number(service.quantity) || 1);
			items.push({
				id: service.service_id || service._id || `service-${service.service_name}`,
				service_id: service.service_id || service._id,
				service_name: service.service_name,
				name: service.service_name,
				quantity,
				unit_price: unitPrice,
				amount: toMoneyNumber(unitPrice * quantity),
				currency: normalizeCurrencyCode(
					service.currency || consultation?.currency,
					fallbackCurrency
				),
				status: service.status || "pending",
				isConsultation: false,
				isPrescribed: true,
				isNewRow: false,
				source: "prescribed",
			});
		});
	}

	return items;
};

export const extractAdditionalInvestigationsFromConsultation = (
	consultation = {},
	fallbackCurrency = "INR",
	allowedCurrencies = []
) => {
	const additionalServices = consultation?.additional_services || [];
	if (!Array.isArray(additionalServices)) return [];

	return additionalServices
		.filter((service) => String(service.service_type || "").toLowerCase() === "laboratory")
		.map((service) => {
			const unitPrice = toMoneyNumber(service.price ?? service.charges ?? 0);
			const quantity = Math.max(1, Number(service.quantity) || 1);
			return {
				id: service.service_id || service._id || `investigation-${service.service_name}`,
				test_id: service.service_id || service._id,
				test_name: service.service_name,
				name: service.service_name,
				quantity,
				unit_price: unitPrice,
				amount: toMoneyNumber(unitPrice * quantity),
				currency: normalizeCurrencyCode(
					service.currency || consultation?.currency,
					fallbackCurrency,
					allowedCurrencies
				),
				type: service.service_type || "laboratory",
				source: "prescribed",
				isPrescribed: true,
				isNewRow: false,
			};
		});
};

const getBillingLinePreTaxAmount = (item, quantity, unitPrice) => {
	const qty = Math.max(1, Number(quantity) || 1);
	const price = toMoneyNumber(unitPrice ?? item.price ?? item.unit_price ?? 0);
	return toMoneyNumber(item.sub_total ?? item.amount ?? price * qty);
};

const mapBillingLineToServiceItem = (item, index, { isConsultation = false } = {}) => {
	const quantity = Math.max(1, Number(item.quantity) || 1);
	const unitPrice = toMoneyNumber(item.price ?? item.unit_price ?? 0);
	const amount = getBillingLinePreTaxAmount(item, quantity, unitPrice);
	const currency = normalizeCurrencyCode(item.currency, "INR");

	return {
		id: isConsultation
			? `billing-consultation-${index}`
			: item.service_id || `billing-service-${index}`,
		service_id: isConsultation ? null : item.service_id || item._id,
		service_name: item.consultation_name || item.service_name || item.name || "Service",
		name: item.consultation_name || item.service_name || item.name || "Service",
		quantity,
		unit_price: unitPrice,
		amount,
		currency,
		isConsultation,
		isPrescribed: true,
		isNewRow: false,
		source: item.source || "prescribed",
	};
};

const mapBillingLineToInvestigationItem = (item, index) => {
	const quantity = Math.max(1, Number(item.quantity) || 1);
	const unitPrice = toMoneyNumber(item.price ?? item.unit_price ?? 0);
	const amount = getBillingLinePreTaxAmount(item, quantity, unitPrice);

	return {
		id:
			item._id ||
			item.order_id ||
			item.investigation_order_id ||
			`billing-investigation-${item.test_id || "test"}-${index}`,
		_id: item._id,
		order_id: item.order_id,
		investigation_order_id: item.investigation_order_id,
		investigation_id: item.investigation_id,
		test_id: item.test_id || item._id,
		test_name: item.test_name || item.name || "Investigation",
		name: item.test_name || item.name || "Investigation",
		quantity,
		unit_price: unitPrice,
		amount,
		currency: normalizeCurrencyCode(item.currency, "INR"),
		type: item.type || "laboratory",
		source: item.source || "prescribed",
		status: item.status,
		createdAt: item.createdAt,
		isPrescribed: true,
		isNewRow: false,
	};
};

export const buildServiceItemsFromBilling = (billing = {}, fallbackCurrency = "INR") => {
	const consultationItems = (billing.consultation || []).map((item, index) =>
		mapBillingLineToServiceItem(item, index, { isConsultation: true })
	);
	const generalServiceItems = (billing.general_services || []).map((item, index) =>
		mapBillingLineToServiceItem(item, index, { isConsultation: false })
	);
	return [...consultationItems, ...generalServiceItems].map((item) => ({
		...item,
		currency: normalizeCurrencyCode(item.currency, fallbackCurrency),
	}));
};

export const buildInvestigationItemsFromBilling = (billing = {}, fallbackCurrency = "INR") =>
	(billing.investigations || []).map((item, index) => ({
		...mapBillingLineToInvestigationItem(item, index),
		currency: normalizeCurrencyCode(item.currency, fallbackCurrency),
	}));

/**
 * Normalize pharmacy lines from saved billing.
 * POS stores tax *percentage* in `tax` (via buildSectionBillingItemFields);
 * some payloads also store tax *amount* in `tax` / `tax_amount`.
 */
export const buildPharmacyItemsFromBilling = (items = []) =>
	(items || []).map((item) => {
		const qty = Math.max(1, Number(item.qty ?? item.quantity) || 1);
		const unitPrice = toMoneyNumber(item.unit_price ?? item.price ?? 0);
		const netTotal = toMoneyNumber(
			item.net_total ?? item.amount ?? unitPrice * qty
		);
		const taxField = toMoneyNumber(item.tax);
		let taxPercentage = toMoneyNumber(item.tax_percentage);
		let subTotal = toMoneyNumber(item.sub_total);
		let taxAmount = toMoneyNumber(item.tax_amount);

		// POS convention: `tax` is percentage when tax_amount is absent.
		if (taxPercentage <= 0 && item.tax_amount == null && taxField > 0) {
			taxPercentage = taxField;
		}

		if (subTotal <= 0) {
			if (taxPercentage > 0 && netTotal > 0) {
				subTotal = toMoneyNumber(netTotal / (1 + taxPercentage / 100));
			} else {
				subTotal = toMoneyNumber(item.amount ?? unitPrice * qty);
			}
		}

		if (taxAmount <= 0 && netTotal > subTotal) {
			taxAmount = toMoneyNumber(netTotal - subTotal);
		}
		if (taxAmount <= 0 && taxPercentage > 0 && subTotal > 0) {
			taxAmount = toMoneyNumber((subTotal * taxPercentage) / 100);
		}
		// Explicit amount in `tax` when percentage already provided separately.
		if (
			taxAmount <= 0 &&
			taxField > 0 &&
			item.tax_percentage != null &&
			taxField !== taxPercentage
		) {
			taxAmount = taxField;
		}

		if (taxPercentage <= 0 && taxAmount > 0 && subTotal > 0) {
			taxPercentage = toMoneyNumber((taxAmount / subTotal) * 100);
		}

		const resolvedNet =
			netTotal > 0 ? netTotal : toMoneyNumber(subTotal + taxAmount);

		return {
			...item,
			item_id: item.item_id,
			stock_id: item.stock_id,
			item: item.item || item.brand_name,
			brand_name: item.brand_name || item.item,
			medicine_name: item.medicine_name,
			qty,
			unit_price: unitPrice,
			sub_total: subTotal,
			tax: taxAmount,
			tax_percentage: taxPercentage,
			tax_amount: taxAmount,
			net_total: resolvedNet,
			amount: toMoneyNumber(item.amount ?? resolvedNet),
			batch_number: item.batch_number,
			product_form: item.product_form,
			currency: normalizeCurrencyCode(item.currency, "INR"),
			date: item.date || item.sale_date || "",
			discount_type: item.discount_type,
			discount: item.discount,
			tax_type: item.tax_type,
		};
	});

export const buildPharmacyTotalsFromBilling = (billing = {}) => {
	const summary = billing?.summary || {};
	return {
		subtotal: toMoneyNumber(summary.pharmacy_subtotal ?? summary.pharmacy_taxable_total ?? 0),
		netTotal: toMoneyNumber(summary.pharmacy_total ?? summary.pharmacy_net_total ?? 0),
		discount: toMoneyNumber(summary.pharmacy_discount ?? 0),
		tax: toMoneyNumber(summary.pharmacy_tax ?? 0),
		CGST: toMoneyNumber(summary.pharmacy_CGST ?? 0),
		SGST: toMoneyNumber(summary.pharmacy_SGST ?? 0),
		IGST: toMoneyNumber(summary.pharmacy_IGST ?? 0),
		VAT: toMoneyNumber(summary.pharmacy_VAT ?? 0),
		transport: toMoneyNumber(summary.transport ?? 0),
	};
};

/** Previously dispensed pharmacy lines from an existing billing object (re-dispense / add-on sale). */
export const getPriorPharmacyItemsFromPrescription = (prescription) => {
	if (!prescription || typeof prescription !== "object") return [];

	const fromBilling = hasPatientAccountBilling(prescription)
		? buildPharmacyItemsFromBilling(prescription?.billing?.pharmacy_items)
		: [];
	if (fromBilling.length > 0) {
		return fromBilling.map((item) => ({ ...item, is_prior: true }));
	}

	// Same fallbacks as Collect Payment: invoice / package pharmacy service lines.
	const invoiceServices = prescription?.appointment_invoice?.services || [];
	const mapInvoicePharmacyLine = (item, index, idPrefix) => {
		const qty = Math.max(1, Number(item.quantity) || 1);
		const unitPrice = toMoneyNumber(item.price ?? 0);
		const amount = toMoneyNumber(item.amount ?? unitPrice * qty);
		const subTotal = toMoneyNumber(item.sub_total ?? amount);
		const netTotal = toMoneyNumber(item.net_total ?? amount);
		const taxPercentage = toMoneyNumber(item.tax_percentage ?? 0);
		let taxAmount = toMoneyNumber(item.tax_amount ?? item.tax ?? 0);
		if (taxAmount <= 0 && netTotal > subTotal) {
			taxAmount = toMoneyNumber(netTotal - subTotal);
		}
		if (taxAmount <= 0 && taxPercentage > 0 && subTotal > 0) {
			taxAmount = toMoneyNumber((subTotal * taxPercentage) / 100);
		}
		return {
			item_id: item.service_id || item._id || `${idPrefix}-${index}`,
			stock_id: item.stock_id || "",
			item: item.service_name || "-",
			brand_name: item.service_name || "-",
			medicine_name: item.medicine_name || "",
			qty,
			unit_price: unitPrice,
			sub_total: subTotal,
			tax: taxAmount,
			tax_percentage: taxPercentage,
			tax_amount: taxAmount,
			net_total: netTotal,
			amount,
			currency: normalizeCurrencyCode(
				item.currency || prescription?.appointment_invoice?.currency,
				"INR"
			),
			date: item.date || item.sale_date || "",
			discount_type: item.discount_type,
			discount: item.discount,
			tax_type: item.tax_type,
			is_prior: true,
		};
	};

	const fromInvoice = invoiceServices
		.filter((item) => String(item.service_type || "").toLowerCase() === "pharmacy")
		.map((item, index) => mapInvoicePharmacyLine(item, index, "pharmacy-invoice"));
	if (fromInvoice.length > 0) return fromInvoice;

	const pkgServices =
		prescription?.package_payment_details?.payment?.services ||
		prescription?.package_payment_details?.treatment_package_payment?.invoice?.services ||
		[];

	return pkgServices
		.filter((item) => String(item.service_type || "").toLowerCase() === "pharmacy")
		.map((item, index) => mapInvoicePharmacyLine(item, index, "pharmacy-pkg"));
};

/** Raw prior pharmacy rows for sale payload (preserve original billing fields when present). */
export const getPriorPharmacyPayloadItems = (prescription) => {
	const billingItems = prescription?.billing?.pharmacy_items;
	if (Array.isArray(billingItems) && billingItems.length > 0) {
		return billingItems;
	}
	return getPriorPharmacyItemsFromPrescription(prescription).map(
		({ is_prior: _isPrior, ...item }) => item
	);
};

export const mergePharmacyTotals = (prior = {}, current = {}) => ({
	subtotal: toMoneyNumber((prior.subtotal || 0) + (current.subtotal || 0)),
	netTotal: toMoneyNumber((prior.netTotal || 0) + (current.netTotal || 0)),
	discount: toMoneyNumber((prior.discount || 0) + (current.discount || 0)),
	tax: toMoneyNumber((prior.tax || 0) + (current.tax || 0)),
	CGST: toMoneyNumber((prior.CGST || 0) + (current.CGST || 0)),
	SGST: toMoneyNumber((prior.SGST || 0) + (current.SGST || 0)),
	IGST: toMoneyNumber((prior.IGST || 0) + (current.IGST || 0)),
	VAT: toMoneyNumber((prior.VAT || 0) + (current.VAT || 0)),
	transport: toMoneyNumber((prior.transport || 0) + (current.transport || 0)),
});

export const appendPharmacyItemsForBilling = (priorItems = [], cartItems = []) => [
	...(priorItems || []),
	...(cartItems || []),
];

export const getSectionDiscountFromSummary = (summary = {}, prefix = "services") => {
	const rawType = summary[`${prefix}_discount_type`] || "Percentage";
	const normalized = String(rawType).toLowerCase();
	return {
		discountType: normalized === "fixed" || normalized === "flat" ? "Fixed" : "Percentage",
		discountValue: toMoneyNumber(summary[`${prefix}_discount_value`] ?? 0),
	};
};

export const getTaxConfigFromBillingSummary = (
	summary = {},
	prefix = "services",
	posTaxType = "gst"
) => {
	const netTaxable = toMoneyNumber(summary[`${prefix}_net_taxable`] ?? 0);
	const tax = toMoneyNumber(summary[`${prefix}_tax`] ?? 0);
	const taxPercentage =
		netTaxable > 0 && tax > 0 ? toMoneyNumber((tax / netTaxable) * 100) : 0;

	return syncGstTaxConfig({
		chargesTax: tax > 0,
		taxType: String(posTaxType || "gst").toLowerCase(),
		taxPercentage,
		cgstPercentage: 0,
		sgstPercentage: 0,
		igstPercentage: 0,
	});
};

export const hasPatientAccountBilling = (record) => {
	const billing = record?.billing;
	if (!billing || typeof billing !== "object") return false;
	return Object.keys(billing).length > 0;
};

const isLabInvoiceServiceType = (serviceType) => {
	const type = String(serviceType || "").toLowerCase();
	return type === "laboratory" || type === "lab";
};

const findBillingServiceLine = (billing = {}, service = {}) => {
	const serviceType = String(service.service_type || "").toLowerCase();
	if (serviceType === "consultation") {
		const name = service.service_name || service.name;
		return (billing.consultation || []).find(
			(item) => (item.consultation_name || item.service_name || item.name) === name
		);
	}
	const serviceId = String(service.service_id || service._id || "");
	if (!serviceId) return null;
	return (billing.general_services || []).find(
		(item) => String(item.service_id || item._id) === serviceId
	);
};

const findBillingInvestigationLine = (billing = {}, service = {}) => {
	const serviceId = String(service.service_id || service._id || "");
	if (!serviceId) return null;
	return (billing.investigations || []).find(
		(item) => String(item.test_id || item.service_id || item._id) === serviceId
	);
};

const overlayBillingServiceAmounts = (item, billingLine) => {
	if (!billingLine) return item;
	const quantity = Math.max(1, Number(billingLine.quantity ?? item.quantity) || 1);
	const unitPrice = toMoneyNumber(
		billingLine.price ?? billingLine.unit_price ?? item.unit_price ?? 0
	);
	const amount = getBillingLinePreTaxAmount(billingLine, quantity, unitPrice);
	return {
		...item,
		quantity,
		unit_price: unitPrice,
		amount,
		currency: normalizeCurrencyCode(billingLine.currency || item.currency, item.currency),
		status: billingLine.status || item.status,
		source: billingLine.source || item.source,
	};
};

const mergeInvestigationWithBillingLine = (invoiceItem, billingLine, index) => {
	if (!billingLine) return invoiceItem;
	const billingItem = mapBillingLineToInvestigationItem(billingLine, index);
	return {
		...billingItem,
		id: invoiceItem.id,
		test_id: invoiceItem.test_id || billingItem.test_id,
		test_name: invoiceItem.test_name || billingItem.test_name,
		currency: normalizeCurrencyCode(billingItem.currency, invoiceItem.currency),
		// Invoice-linked lines were billed in a prior payment; keep them out of payload.
		source: "prescribed",
		isPrescribed: true,
		isNewRow: false,
	};
};

/**
 * Route Collect Payment lines using appointment_invoice.services.service_type:
 * consultation/general → services table; laboratory → investigations table.
 * Billing line amounts are merged when present.
 */
export const buildCollectPaymentBillingItemsFromRecord = (
	record = {},
	fallbackCurrency = "INR",
	allowedCurrencies = []
) => {
	const appointmentInvoice = record?.appointment_invoice || {};
	const invoiceServices = appointmentInvoice.services || [];
	if (!Array.isArray(invoiceServices) || invoiceServices.length === 0) {
		return null;
	}

	const billing = record?.billing || {};
	const baseCurrency = normalizeCurrencyCode(
		appointmentInvoice.currency || fallbackCurrency,
		fallbackCurrency
	);

	const serviceItems = invoiceServices
		.filter((service) => {
			const serviceType = String(service.service_type || "").toLowerCase();
			return (
				serviceType !== "laboratory" &&
				serviceType !== "lab" &&
				serviceType !== "pharmacy" &&
				serviceType !== "treatment_package"
			);
		})
		.map((service, index) => {
			const item = mapAppointmentInvoiceServiceToItem(
				service,
				index,
				fallbackCurrency,
				baseCurrency
			);
			return overlayBillingServiceAmounts(item, findBillingServiceLine(billing, service));
		});

	const investigationItems = invoiceServices
		.filter((service) => isLabInvoiceServiceType(service.service_type))
		.map((service, index) => {
			const unitPrice = toMoneyNumber(service.price ?? service.charges ?? 0);
			const quantity = Math.max(1, Number(service.quantity) || 1);
			const invoiceItem = {
				id: service._id || service.service_id || `invoice-investigation-${index}`,
				test_id: service.service_id || service._id,
				test_name: service.service_name || service.name || "Investigation",
				name: service.service_name || service.name || "Investigation",
				quantity,
				unit_price: unitPrice,
				amount: toMoneyNumber(unitPrice * quantity),
				currency: normalizeCurrencyCode(
					service.currency || baseCurrency,
					fallbackCurrency,
					allowedCurrencies
				),
				type: service.service_type || "laboratory",
				status: service.status || "pending",
				source: "prescribed",
				isPrescribed: true,
				isNewRow: false,
			};
			return mergeInvestigationWithBillingLine(
				invoiceItem,
				findBillingInvestigationLine(billing, service),
				index
			);
		});

	return { serviceItems, investigationItems };
};

/** Only lab investigations added in the current Collect Payment session belong in billing.investigations. */
export const filterNewBillingInvestigationsForPayload = (items = []) =>
	items.filter((item) => item.isNewRow === true);

const getBillingServiceIdentity = (item = {}) => {
	if (item.isConsultation || String(item.service_type || "").toLowerCase() === "consultation") {
		return `consultation:${item.service_name || item.name || item.consultation_name || ""}`;
	}
	const serviceId = item.service_id || item.test_id || item._id || "";
	if (serviceId) return `id:${serviceId}`;
	return `name:${item.service_name || item.name || item.test_name || ""}`;
};

/** Append billing-only lines that are not already present from appointment_invoice. */
export const mergeBillingExtrasIntoServiceItems = (
	invoiceServiceItems = [],
	billingServiceItems = []
) => {
	const existing = new Set((invoiceServiceItems || []).map(getBillingServiceIdentity));
	const extras = (billingServiceItems || []).filter(
		(item) => !existing.has(getBillingServiceIdentity(item))
	);
	return [...(invoiceServiceItems || []), ...extras];
};

export const mergeBillingExtrasIntoInvestigationItems = (
	invoiceInvestigationItems = [],
	billingInvestigationItems = []
) => {
	const existing = new Set(
		(invoiceInvestigationItems || []).map(
			(item) => String(item.test_id || item.service_id || item._id || item.test_name || "")
		)
	);
	const extras = (billingInvestigationItems || []).filter((item) => {
		const key = String(item.test_id || item.service_id || item._id || item.test_name || "");
		return key && !existing.has(key);
	});
	return [...(invoiceInvestigationItems || []), ...extras];
};

/**
 * Load billable services/labs for POS Billing & Collect Payment.
 * Prefer appointment_invoice routing, then billing lines, and always include
 * billing-only extras (newly added services) that are not on the invoice yet.
 */
export const initializePatientAccountBillingItems = (
	record = {},
	fallbackCurrency = "INR",
	allowedCurrencies = []
) => {
	const fromAppointmentInvoice = buildCollectPaymentBillingItemsFromRecord(
		record,
		fallbackCurrency,
		allowedCurrencies
	);
	const billingServiceItems = hasPatientAccountBilling(record)
		? buildServiceItemsFromBilling(record.billing, fallbackCurrency)
		: [];
	const billingInvestigationItems = hasPatientAccountBilling(record)
		? buildInvestigationItemsFromBilling(record.billing, fallbackCurrency)
		: [];

	if (fromAppointmentInvoice) {
		const invoiceServices = fromAppointmentInvoice.serviceItems || [];
		const invoiceInvestigations = fromAppointmentInvoice.investigationItems || [];

		// Invoice may only contain pharmacy/package lines — fall back to billing
		// for consultation/general/lab the same way Patient Detail / Collect Payment do.
		const serviceItems =
			invoiceServices.length > 0
				? mergeBillingExtrasIntoServiceItems(invoiceServices, billingServiceItems)
				: billingServiceItems.length > 0
					? billingServiceItems
					: invoiceServices;

		const investigationItems =
			invoiceInvestigations.length > 0
				? mergeBillingExtrasIntoInvestigationItems(
						invoiceInvestigations,
						billingInvestigationItems
					)
				: billingInvestigationItems.length > 0
					? billingInvestigationItems
					: invoiceInvestigations;

		if (serviceItems.length > 0 || investigationItems.length > 0) {
			return { serviceItems, investigationItems };
		}
	}

	if (hasPatientAccountBilling(record)) {
		return {
			serviceItems: billingServiceItems,
			investigationItems: billingInvestigationItems,
		};
	}

	// Do not auto-load EMR `investigation[]` into the billable table — those are
	// suggestions only (see extractSuggestedInvestigations). Load billed/invoice labs.
	const prescribedInvestigations =
		Array.isArray(record?.appointment_invoice?.services) &&
		record.appointment_invoice.services.length > 0
			? extractInvestigationsFromAppointmentInvoice(
					record.appointment_invoice,
					fallbackCurrency,
					allowedCurrencies
				)
			: extractAdditionalInvestigationsFromConsultation(
					record.consultation,
					fallbackCurrency,
					allowedCurrencies
				);

	return {
		serviceItems: buildInitialServiceItems(record, fallbackCurrency),
		investigationItems: prescribedInvestigations,
	};
};

export const enrichServiceItemsWithMaster = (
	items = [],
	masterList = [],
	fallbackCurrency = "INR",
	allowedCurrencies = []
) => {
	const masterMap = buildGeneralServiceMasterMap(masterList);
	return items.map((item) => {
		const quantity = Math.max(1, Number(item.quantity) || 1);
		const master = item.service_id ? masterMap.get(String(item.service_id)) : null;

		if (item.isConsultation) {
			const unitPrice =
				item.unit_price !== undefined && item.unit_price !== null && item.unit_price !== ""
					? toMoneyNumber(item.unit_price)
					: item.price > 0
						? toMoneyNumber(item.price)
						: item.amount > 0
							? toMoneyNumber(item.amount / quantity)
							: 0;
			return {
				...item,
				quantity,
				unit_price: unitPrice,
				amount: toMoneyNumber(unitPrice * quantity),
			};
		}

		const unitPrice = resolveServiceUnitPrice(item, quantity, master);
		return {
			...item,
			quantity,
			unit_price: unitPrice.unitPrice,
			amount: toMoneyNumber(unitPrice.unitPrice * quantity),
			currency: normalizeCurrencyCode(
				unitPrice.fromItem ? item.currency : master?.currency || item.currency,
				fallbackCurrency,
				allowedCurrencies
			),
		};
	});
};

export const createEmptyServiceRow = (fallbackCurrency = "INR") => ({
	id: `new-service-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
	service_id: "",
	service_name: "",
	quantity: 1,
	unit_price: 0,
	amount: 0,
	currency: fallbackCurrency,
	isConsultation: false,
	isPrescribed: false,
	isNewRow: true,
	source: "added",
});

export const createEmptyInvestigationRow = (fallbackCurrency = "INR") => ({
	id: `new-investigation-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
	test_id: "",
	test_name: "",
	quantity: 1,
	unit_price: 0,
	amount: 0,
	currency: fallbackCurrency,
	type: "laboratory",
	status: "pending",
	isPrescribed: false,
	isNewRow: true,
	source: "added",
});

export const getBillingLineUnitPrice = (item = {}) => {
	if (item.unit_price !== undefined && item.unit_price !== null && item.unit_price !== "") {
		return toMoneyNumber(item.unit_price);
	}
	const quantity = Math.max(1, Number(item.quantity) || 1);
	return toMoneyNumber((item.amount || 0) / quantity);
};

export const recalculateBillingLine = (item = {}, quantity) => {
	const qty = Math.max(1, Number(quantity) || 1);
	const unitPrice = getBillingLineUnitPrice(item);
	return {
		quantity: qty,
		unit_price: unitPrice,
		amount: toMoneyNumber(unitPrice * qty),
	};
};

export const getInvestigationUnitPrice = getBillingLineUnitPrice;
export const getServiceUnitPrice = getBillingLineUnitPrice;

export const recalculateInvestigationLine = recalculateBillingLine;
export const recalculateServiceLine = recalculateBillingLine;

export const buildPharmacyItemsFromCart = (cart = []) =>
	cart.map((item) => ({
		item_id: item._id,
		stock_id: item.selectedBatchId,
		item: item.brandName || item.saltName,
		brand_name: item.brandName,
		medicine_name: item.saltName,
		qty: item.quantity,
		unit_price: toMoneyNumber(item.salePrice || 0),
		amount: toMoneyNumber((item.salePrice || 0) * (item.quantity || 0)),
		batch_number: item.batchNumber,
		product_form: item.productForm,
	}));

export const sumPharmacyLineAmounts = (
	pharmacyItems = [],
	targetCurrency,
	convertCurrency,
	pharmacyCurrency = "INR",
	allowedCurrencies = []
) =>
	sumConvertedLineAmounts(
		pharmacyItems.map((item) => ({
			amount: item.amount,
			currency: pharmacyCurrency,
		})),
		targetCurrency,
		convertCurrency,
		pharmacyCurrency,
		allowedCurrencies
	);

export const sumServiceItemsSubtotal = (
	serviceItems = [],
	targetCurrency,
	convertCurrency,
	pharmacyCurrency = "INR",
	allowedCurrencies = []
) =>
	sumConvertedLineAmounts(
		serviceItems,
		targetCurrency,
		convertCurrency,
		pharmacyCurrency,
		allowedCurrencies
	);

export const sumInvestigationItemsSubtotal = (
	investigationItems = [],
	targetCurrency,
	convertCurrency,
	pharmacyCurrency = "INR",
	allowedCurrencies = []
) =>
	sumConvertedLineAmounts(
		investigationItems,
		targetCurrency,
		convertCurrency,
		pharmacyCurrency,
		allowedCurrencies
	);

export const createEmptySectionTaxConfig = (posTaxType = "gst") => ({
	chargesTax: false,
	taxType: String(posTaxType || "gst").toLowerCase(),
	taxPercentage: 0,
	cgstPercentage: 0,
	sgstPercentage: 0,
	igstPercentage: 0,
});

export const createEmptyServicesTaxConfig = createEmptySectionTaxConfig;

export const getPrescriptionBillingFlags = (record) => {
	const source = record || {};
	return {
		showLabSection: source.is_clinic_collecting_lab_payment === true,
		showServicesTax: source.is_clinic_service_tax === true,
		showLabTax:
			source.is_clinic_collecting_lab_payment === true &&
			source.is_laboratory_service_tax === true,
	};
};

export const getEffectiveSectionTaxConfig = (taxConfig, applyTax, posTaxType = "gst") =>
	applyTax ? taxConfig : createEmptySectionTaxConfig(posTaxType);

export const syncGstTaxConfig = (config = {}) => {
	const taxType = String(config.taxType || "gst").toLowerCase();
	const taxPercentage = toMoneyNumber(config.taxPercentage);
	if (taxType === "vat") {
		return { ...config, taxPercentage };
	}
	const half = taxPercentage > 0 ? toMoneyNumber(taxPercentage / 2) : 0;
	return {
		...config,
		taxType,
		taxPercentage,
		cgstPercentage: half,
		sgstPercentage: half,
		igstPercentage: taxPercentage,
	};
};

export const hasApplicableSectionTax = (taxConfig = {}) => {
	const taxType = String(taxConfig.taxType || "gst").toLowerCase();
	return toMoneyNumber(taxConfig.taxPercentage) > 0;
};

export const hasApplicableServicesTax = hasApplicableSectionTax;

export const getSectionTaxConfig = (prescription = {}, posTaxType = "gst") => {
	const inv = prescription?.appointment_invoice || {};
	const cgstFromInv =
		inv.cgst != null && inv.cgst !== "" ? toMoneyNumber(inv.cgst) : 0;
	const sgstFromInv =
		inv.sgst != null && inv.sgst !== "" ? toMoneyNumber(inv.sgst) : 0;
	const igstFromInv =
		inv.igst != null && inv.igst !== "" ? toMoneyNumber(inv.igst) : 0;
	const taxPercentage = toMoneyNumber(
		inv.tax_percentage ||
			(cgstFromInv + sgstFromInv > 0 ? cgstFromInv + sgstFromInv : 0) ||
			igstFromInv ||
			0
	);

	return syncGstTaxConfig({
		chargesTax: Boolean(inv.charges_tax),
		taxType: String(inv.tax_type || posTaxType || "gst").toLowerCase(),
		taxPercentage,
		cgstPercentage: 0,
		sgstPercentage: 0,
		igstPercentage: 0,
	});
};

export const getServicesTaxConfig = getSectionTaxConfig;
export const getInvestigationTaxConfig = getSectionTaxConfig;

export const calculateServicesDiscountAmount = (
	subtotal,
	discountType = "Percentage",
	discountValue = 0
) => {
	const base = toMoneyNumber(subtotal);
	const value = toMoneyNumber(discountValue);
	if (value <= 0 || base <= 0) return 0;
	if (discountType === "Percentage") {
		return toMoneyNumber(Math.min(base, (base * value) / 100));
	}
	return toMoneyNumber(Math.min(base, value));
};

export const calculateSectionBillingTotals = ({
	subtotal = 0,
	discountType = "Percentage",
	discountValue = 0,
	taxConfig = {},
	isSameState = true,
}) => {
	const lineSubtotal = toMoneyNumber(subtotal);
	const discount = calculateServicesDiscountAmount(lineSubtotal, discountType, discountValue);
	const netTaxable = toMoneyNumber(Math.max(0, lineSubtotal - discount));

	let CGST = 0;
	let SGST = 0;
	let IGST = 0;
	let VAT = 0;
	let tax = 0;

	if (hasApplicableSectionTax(taxConfig) && netTaxable > 0) {
		if (taxConfig.taxType === "vat") {
			VAT = toMoneyNumber((netTaxable * (taxConfig.taxPercentage || 0)) / 100);
			tax = VAT;
		} else if (isSameState) {
			const gstRate = toMoneyNumber(taxConfig.taxPercentage || 0);
			const halfRate = toMoneyNumber(gstRate / 2);
			CGST = toMoneyNumber((netTaxable * halfRate) / 100);
			SGST = toMoneyNumber((netTaxable * halfRate) / 100);
			tax = toMoneyNumber(CGST + SGST);
		} else {
			IGST = toMoneyNumber((netTaxable * (taxConfig.taxPercentage || 0)) / 100);
			tax = IGST;
		}
	}

	const grandTotal = toMoneyNumber(netTaxable + tax);

	return {
		subtotal: lineSubtotal,
		discount,
		netTaxable,
		tax,
		CGST,
		SGST,
		IGST,
		VAT,
		grandTotal,
		discountType,
		discountValue: toMoneyNumber(discountValue),
		taxConfig,
	};
};

export const calculateServicesBillingTotals = ({
	serviceItems = [],
	discountType = "Percentage",
	discountValue = 0,
	taxConfig = {},
	isSameState = true,
	targetCurrency,
	convertCurrency,
	pharmacyCurrency = "INR",
	allowedCurrencies = [],
}) => {
	const subtotal = sumServiceItemsSubtotal(
		serviceItems,
		targetCurrency,
		convertCurrency,
		pharmacyCurrency,
		allowedCurrencies
	);
	return calculateSectionBillingTotals({
		subtotal,
		discountType,
		discountValue,
		taxConfig,
		isSameState,
	});
};

export const calculateInvestigationBillingTotals = ({
	investigationItems = [],
	discountType = "Percentage",
	discountValue = 0,
	taxConfig = {},
	isSameState = true,
	targetCurrency,
	convertCurrency,
	pharmacyCurrency = "INR",
	allowedCurrencies = [],
}) => {
	const subtotal = sumInvestigationItemsSubtotal(
		investigationItems,
		targetCurrency,
		convertCurrency,
		pharmacyCurrency,
		allowedCurrencies
	);
	return calculateSectionBillingTotals({
		subtotal,
		discountType,
		discountValue,
		taxConfig,
		isSameState,
	});
};

export const buildBillingSummary = ({
	serviceItems = [],
	servicesBilling = null,
	investigationItems = [],
	investigationsBilling = null,
	pharmacyItems = [],
	pharmacyTotals = {},
	pharmacyDiscountType = "Percentage",
	pharmacyDiscountValue = 0,
	targetCurrency,
	convertCurrency,
	pharmacyCurrency = "INR",
	allowedCurrencies = [],
}) => {
	const payCurrency = normalizeCurrencyCode(targetCurrency, pharmacyCurrency, allowedCurrencies);
	const pharmacyCur = normalizeCurrencyCode(pharmacyCurrency, "INR", allowedCurrencies);

	const servicesLineSubtotal =
		servicesBilling?.subtotal ??
		sumServiceItemsSubtotal(
			serviceItems,
			payCurrency,
			convertCurrency,
			pharmacyCur,
			allowedCurrencies
		);
	const servicesGrandTotal =
		servicesBilling?.grandTotal ?? servicesLineSubtotal;
	const investigationLineSubtotal =
		investigationsBilling?.subtotal ??
		sumInvestigationItemsSubtotal(
			investigationItems,
			payCurrency,
			convertCurrency,
			pharmacyCur,
			allowedCurrencies
		);
	const investigationGrandTotal =
		investigationsBilling?.grandTotal ?? investigationLineSubtotal;
	const pharmacyTaxableTotal = convertLineAmount(
		pharmacyTotals.subtotal || 0,
		pharmacyCur,
		payCurrency,
		convertCurrency,
		pharmacyCur,
		allowedCurrencies
	);
	const pharmacyLineTotal = sumPharmacyLineAmounts(
		pharmacyItems,
		payCurrency,
		convertCurrency,
		pharmacyCur,
		allowedCurrencies
	);
	const pharmacyNetTotal = convertLineAmount(
		pharmacyTotals.netTotal || 0,
		pharmacyCur,
		payCurrency,
		convertCurrency,
		pharmacyCur,
		allowedCurrencies
	);
	const discount = convertLineAmount(
		pharmacyTotals.discount || 0,
		pharmacyCur,
		payCurrency,
		convertCurrency,
		pharmacyCur,
		allowedCurrencies
	);
	const tax = convertLineAmount(
		pharmacyTotals.tax || 0,
		pharmacyCur,
		payCurrency,
		convertCurrency,
		pharmacyCur,
		allowedCurrencies
	);
	const transport = convertLineAmount(
		pharmacyTotals.transport || 0,
		pharmacyCur,
		payCurrency,
		convertCurrency,
		pharmacyCur,
		allowedCurrencies
	);
	const pharmacyCGST = convertLineAmount(
		pharmacyTotals.CGST || 0,
		pharmacyCur,
		payCurrency,
		convertCurrency,
		pharmacyCur,
		allowedCurrencies
	);
	const pharmacySGST = convertLineAmount(
		pharmacyTotals.SGST || 0,
		pharmacyCur,
		payCurrency,
		convertCurrency,
		pharmacyCur,
		allowedCurrencies
	);
	const pharmacyIGST = convertLineAmount(
		pharmacyTotals.IGST || 0,
		pharmacyCur,
		payCurrency,
		convertCurrency,
		pharmacyCur,
		allowedCurrencies
	);
	const pharmacyVAT = convertLineAmount(
		pharmacyTotals.VAT || 0,
		pharmacyCur,
		payCurrency,
		convertCurrency,
		pharmacyCur,
		allowedCurrencies
	);
	const pharmacyNetTaxable = toMoneyNumber(pharmacyTaxableTotal - discount);

	const subtotal = toMoneyNumber(
		servicesLineSubtotal + investigationLineSubtotal + pharmacyTaxableTotal
	);
	const grandTotal = toMoneyNumber(
		servicesGrandTotal + investigationGrandTotal + pharmacyNetTotal
	);

	return {
		services_subtotal: servicesLineSubtotal,
		services_discount: servicesBilling?.discount ?? 0,
		services_net_taxable: servicesBilling?.netTaxable ?? servicesLineSubtotal,
		services_tax: servicesBilling?.tax ?? 0,
		services_CGST: servicesBilling?.CGST ?? 0,
		services_SGST: servicesBilling?.SGST ?? 0,
		services_IGST: servicesBilling?.IGST ?? 0,
		services_VAT: servicesBilling?.VAT ?? 0,
		services_discount_type: servicesBilling?.discountType ?? "Percentage",
		services_discount_value: servicesBilling?.discountValue ?? 0,
		services_total: servicesGrandTotal,
		investigation_subtotal: investigationLineSubtotal,
		investigation_discount: investigationsBilling?.discount ?? 0,
		investigation_net_taxable: investigationsBilling?.netTaxable ?? investigationLineSubtotal,
		investigation_tax: investigationsBilling?.tax ?? 0,
		investigation_CGST: investigationsBilling?.CGST ?? 0,
		investigation_SGST: investigationsBilling?.SGST ?? 0,
		investigation_IGST: investigationsBilling?.IGST ?? 0,
		investigation_VAT: investigationsBilling?.VAT ?? 0,
		investigation_discount_type: investigationsBilling?.discountType ?? "Percentage",
		investigation_discount_value: investigationsBilling?.discountValue ?? 0,
		investigation_total: investigationGrandTotal,
		pharmacy_subtotal: pharmacyTaxableTotal,
		pharmacy_discount: discount,
		pharmacy_net_taxable: pharmacyNetTaxable,
		pharmacy_tax: tax,
		pharmacy_CGST: pharmacyCGST,
		pharmacy_SGST: pharmacySGST,
		pharmacy_IGST: pharmacyIGST,
		pharmacy_VAT: pharmacyVAT,
		pharmacy_discount_type: pharmacyDiscountType,
		pharmacy_discount_value: toMoneyNumber(pharmacyDiscountValue),
		pharmacy_total: pharmacyNetTotal,
		pharmacy_taxable_total: pharmacyTaxableTotal,
		pharmacy_line_total: pharmacyLineTotal,
		pharmacy_net_total: pharmacyNetTotal,
		subtotal,
		discount,
		tax,
		transport,
		grand_total: grandTotal,
		currency: payCurrency,
	};
};

export const buildGeneralServiceOptions = (
	masterList = [],
	getCurrencySymbol,
	fallbackCurrency = "INR",
	allowedCurrencies = []
) =>
	masterList.map((item) => {
		const id = item.service_id || item._id || item.id;
		const name = item.service_name || item.name;
		const currency = normalizeCurrencyCode(item.currency, fallbackCurrency, allowedCurrencies);
		return {
			value: id,
			label: formatMasterOptionLabel(
				name,
				item.price ?? item.charges ?? item.amount ?? 0,
				currency,
				getCurrencySymbol,
				fallbackCurrency,
				allowedCurrencies
			),
		};
	});

export const buildInvestigationOptions = (
	masterList = [],
	getCurrencySymbol,
	fallbackCurrency = "INR",
	allowedCurrencies = []
) =>
	masterList.map((item) => {
		const id = getInvestigationMasterId(item);
		const name = item.test_name || item.name;
		const currency = normalizeCurrencyCode(item.currency, fallbackCurrency, allowedCurrencies);
		return {
			value: id,
			label: formatMasterOptionLabel(
				name,
				item.price ?? item.charges ?? item.amount ?? 0,
				currency,
				getCurrencySymbol,
				fallbackCurrency,
				allowedCurrencies
			),
		};
	});

export const investigationFromMaster = (
	item,
	fallbackCurrency = "INR",
	allowedCurrencies = []
) => {
	const id = getInvestigationMasterId(item);
	const currency = normalizeCurrencyCode(item.currency, fallbackCurrency, allowedCurrencies);
	const unitPrice = toMoneyNumber(item.price ?? item.charges ?? item.amount ?? 0);
	const quantity = Math.max(1, Number(item.quantity) || 1);
	return {
		id,
		test_id: id,
		test_name: item.test_name || item.name,
		name: item.test_name || item.name,
		quantity,
		unit_price: unitPrice,
		amount: toMoneyNumber(unitPrice * quantity),
		currency,
		type: item.type || "laboratory",
		source: "added",
		status: "pending",
		isPrescribed: false,
		isNewRow: false,
	};
};

export const generalServiceFromMaster = (
	item,
	fallbackCurrency = "INR",
	allowedCurrencies = []
) => {
	const id = item.service_id || item._id || item.id;
	const currency = normalizeCurrencyCode(item.currency, fallbackCurrency, allowedCurrencies);
	const unitPrice = toMoneyNumber(item.price ?? item.charges ?? item.amount ?? 0);
	const quantity = Math.max(1, Number(item.quantity) || 1);
	return {
		id,
		service_id: id,
		service_name: item.service_name || item.name,
		name: item.service_name || item.name,
		quantity,
		unit_price: unitPrice,
		amount: toMoneyNumber(unitPrice * quantity),
		currency,
		isConsultation: false,
		isPrescribed: false,
		isNewRow: false,
		source: "added",
	};
};

export const resolveBillingTaxType = (...candidates) => {
	for (const value of candidates) {
		if (value === null || value === undefined) continue;
		const normalized = String(value).trim().toLowerCase();
		if (normalized === "gst" || normalized === "vat") return normalized;
	}
	return "";
};

export const formatBillingDiscountType = (discountType = "Percentage") => {
	const normalized = String(discountType).toLowerCase();
	if (normalized === "fixed" || normalized === "flat") return "flat";
	return "percentage";
};

export const getMaxItemTaxPercentage = (itemTaxes = {}) =>
	toMoneyNumber(
		Math.max(
			0,
			...Object.values(itemTaxes || {}).map((tax) =>
				toMoneyNumber(tax?.taxPercentage)
			)
		)
	);

export const buildSectionBillingItemFields = ({
	discountType = "Percentage",
	discountValue = 0,
	taxType = "gst",
	taxPercentage = 0,
	subTotal = 0,
	netTotal = 0,
}) => ({
	discount_type: formatBillingDiscountType(discountType),
	discount: toMoneyNumber(discountValue),
	tax_type: String(taxType || "gst").toLowerCase(),
	tax: toMoneyNumber(taxPercentage),
	sub_total: toMoneyNumber(subTotal),
	net_total: toMoneyNumber(netTotal),
});

export const buildPharmacyItemBillingFieldsMap = ({
	cart = [],
	itemTaxes = {},
	discountType = "Percentage",
	discountValue = 0,
	totals = {},
	posTaxType = "gst",
	isSameState = true,
	targetCurrency,
	convertCurrency,
	pharmacyCurrency = "INR",
	allowedCurrencies = [],
}) => {
	const billDiscountRate = getBillDiscountRate(
		totals.subtotal || 0,
		totals.discount || 0
	);
	const payCurrency = normalizeCurrencyCode(targetCurrency, pharmacyCurrency, allowedCurrencies);
	const pharmacyCur = normalizeCurrencyCode(pharmacyCurrency, "INR", allowedCurrencies);
	const fieldsByKey = new Map();

	cart.forEach((item) => {
		const itemKey = `${item._id}-${item.selectedBatchId}`;
		const taxInfo = itemTaxes[itemKey] || {};
		const taxableBefore = toDecimal(taxInfo.taxableAmount || 0);
		const afterDisc = getItemLineAfterBillDiscount(taxableBefore, billDiscountRate);
		const taxRate = taxInfo.taxPercentage || 0;
		const { itemTax } = computeItemTaxOnExclusiveTaxable(
			afterDisc,
			taxRate,
			posTaxType,
			isSameState
		);
		const subTotalSource = toMoneyNumber(afterDisc);
		const netTotalSource = toMoneyNumber(afterDisc.plus(itemTax));

		fieldsByKey.set(
			itemKey,
			buildSectionBillingItemFields({
				discountType,
				discountValue,
				taxType: posTaxType,
				taxPercentage: taxRate,
				subTotal: convertLineAmount(
					subTotalSource,
					pharmacyCur,
					payCurrency,
					convertCurrency,
					pharmacyCur,
					allowedCurrencies
				),
				netTotal: convertLineAmount(
					netTotalSource,
					pharmacyCur,
					payCurrency,
					convertCurrency,
					pharmacyCur,
					allowedCurrencies
				),
			})
		);
	});

	return fieldsByKey;
};

export const normalizeBillingLineAmount = ({
	amount = 0,
	currency,
	baseCurrency,
	convertCurrency,
	pharmacyCurrency = "INR",
	allowedCurrencies = [],
}) => {
	const payloadCurrency = normalizeCurrencyCode(
		baseCurrency,
		pharmacyCurrency,
		allowedCurrencies
	);
	return {
		amount: convertLineAmount(
			amount,
			currency,
			payloadCurrency,
			convertCurrency,
			pharmacyCurrency,
			allowedCurrencies
		),
		currency: payloadCurrency,
	};
};

export const buildSectionLineBillingFieldsMap = ({
	items = [],
	getItemKey = (item) => item.id,
	discountType = "Percentage",
	discountValue = 0,
	taxConfig = {},
	isSameState = true,
	posTaxType = "gst",
	targetCurrency,
	convertCurrency,
	pharmacyCurrency = "INR",
	allowedCurrencies = [],
}) => {
	const payCurrency = normalizeCurrencyCode(targetCurrency, pharmacyCurrency, allowedCurrencies);
	const sectionSubtotal = sumConvertedLineAmounts(
		items,
		payCurrency,
		convertCurrency,
		pharmacyCurrency,
		allowedCurrencies
	);
	const sectionDiscount = calculateServicesDiscountAmount(
		sectionSubtotal,
		discountType,
		discountValue
	);
	const billDiscountRate = getBillDiscountRate(sectionSubtotal, sectionDiscount);
	const taxType = taxConfig?.taxType || posTaxType;
	const taxRate = hasApplicableSectionTax(taxConfig) ? taxConfig.taxPercentage || 0 : 0;
	const fieldsByKey = new Map();

	items.forEach((item) => {
		const lineSubtotal = convertLineAmount(
			item.amount,
			item.currency,
			payCurrency,
			convertCurrency,
			pharmacyCurrency,
			allowedCurrencies
		);
		const lineAfterDiscount = toMoneyNumber(
			getItemLineAfterBillDiscount(toDecimal(lineSubtotal), billDiscountRate)
		);
		const { itemTax } = computeItemTaxOnExclusiveTaxable(
			toDecimal(lineAfterDiscount),
			taxRate,
			taxType,
			isSameState
		);
		const netTotal = toMoneyNumber(toDecimal(lineAfterDiscount).plus(itemTax));

		fieldsByKey.set(
			getItemKey(item),
			buildSectionBillingItemFields({
				discountType,
				discountValue,
				taxType,
				taxPercentage: taxRate,
				subTotal: lineAfterDiscount,
				netTotal,
			})
		);
	});

	return fieldsByKey;
};

export const buildBillingHospitalPayload = (hospital) => {
	if (!hospital || typeof hospital !== "object") return null;

	return {
		name: hospital.name || "",
		logo: hospital.logo || "",
		phone_code: hospital.phone_code || "",
		mobile: hospital.mobile || "",
		email: hospital.email || "",
		address: hospital.address || "",
		profile_picture: hospital.profile_picture || "",
	};
};

export const buildBillingContextFields = ({
	prescription,
	selectedPatient,
	posTaxType,
} = {}) => {
	const appointmentInvoice = prescription?.appointment_invoice || {};

	const patientId =
		prescription?.patient_id ||
		prescription?.patient?._id ||
		prescription?.patient?.id ||
		selectedPatient?._id ||
		selectedPatient?.id ||
		selectedPatient?.patient_id ||
		null;

	const doctorId =
		appointmentInvoice?.doctor_id ||
		prescription?.doctor_id ||
		prescription?.doctor?._id ||
		prescription?.doctor?.id ||
		null;

	const appointmentId =
		appointmentInvoice?.appointment_id ||
		prescription?.appointment_id ||
		null;

	const taxType = resolveBillingTaxType(
		appointmentInvoice?.tax_type,
		prescription?.tax_type,
		posTaxType
	);

	const hospital = buildBillingHospitalPayload(prescription?.hospital);

	return {
		patient_id: patientId,
		doctor_id: doctorId,
		appointment_id: appointmentId,
		tax_type: taxType,
		...(hospital ? { hospital } : {}),
		...(prescription?.payment_qrcode
			? { payment_qrcode: prescription.payment_qrcode }
			: {}),
	};
};

export { isTreatmentPackageInvoice };

export const getTreatmentPackagePrice = (prescription = {}) => {
	const consultation = prescription?.consultation || {};
	const invoice = prescription?.appointment_invoice || {};
	const treatmentPackage = prescription?.package_payment_details?.treatment_package || {};
	return toMoneyNumber(
		consultation.package_price ??
			invoice.package_price ??
			treatmentPackage.total_price ??
			0
	);
};

export const buildInitialTreatmentServiceItems = (
	prescription = {},
	fallbackCurrency = "INR"
) => {
	if (!resolveActivePrescription(prescription)) return [];

	// Load every billable line from appointment_invoice.services (including completed)
	// so Treatment & Other Services and Additional Services stay amount-synced.
	return buildTreatmentAdditionalServices(prescription, {
		billableOnly: false,
		excludePharmacyAndLab: true,
	}).map((service) => ({
		...service,
		isPrescribed: true,
		isNewRow: false,
		source: service.source || "prescribed",
	}));
};

export const buildInitialTreatmentInvestigationItems = (
	prescription = {},
	fallbackCurrency = "INR",
	allowedCurrencies = []
) => {
	const source = prescription || {};
	// EMR investigation[] is suggestion-only — excluded from billable table init.
	const prescribed = buildTreatmentInvestigationItems(source).map((item) => ({
		...item,
		unit_price: toMoneyNumber(item.unit_price ?? item.amount ?? 0),
		amount: toMoneyNumber(item.amount ?? 0),
		currency: normalizeCurrencyCode(item.currency, fallbackCurrency, allowedCurrencies),
		source: "prescribed",
		isPrescribed: true,
		isNewRow: false,
	}));

	const fromInvoice = extractInvestigationsFromAppointmentInvoice(
		source?.appointment_invoice,
		fallbackCurrency,
		allowedCurrencies
	);

	const fromConsultation = extractAdditionalInvestigationsFromConsultation(
		source?.consultation,
		fallbackCurrency,
		allowedCurrencies
	);

	const merged = [...prescribed, ...fromInvoice, ...fromConsultation];
	const seen = new Set();
	return merged.filter((item) => {
		const key = String(item.test_id || item.id);
		if (seen.has(key)) return false;
		seen.add(key);
		return true;
	});
};

const sumServiceItemsByType = (
	serviceItems = [],
	isConsultation,
	targetCurrency,
	convertCurrency,
	pharmacyCurrency,
	allowedCurrencies
) => {
	const filtered = serviceItems.filter((item) =>
		isConsultation ? item.isConsultation : !item.isConsultation
	);
	return sumServiceItemsSubtotal(
		filtered,
		targetCurrency,
		convertCurrency,
		pharmacyCurrency,
		allowedCurrencies
	);
};

export const buildTreatmentBillingSummary = ({
	baseSummary = {},
	prescription = {},
	serviceItems = [],
	packagePrice = 0,
	targetCurrency,
	convertCurrency,
	pharmacyCurrency = "INR",
	allowedCurrencies = [],
}) => {
	const invoice = prescription?.appointment_invoice || {};
	const pharmacyNetTotal = toMoneyNumber(baseSummary.pharmacy_net_total ?? 0);
	const payCurrency = normalizeCurrencyCode(
		targetCurrency || baseSummary.currency,
		pharmacyCurrency,
		allowedCurrencies
	);

	const consultationTotal = sumServiceItemsByType(
		serviceItems,
		true,
		payCurrency,
		convertCurrency,
		pharmacyCurrency,
		allowedCurrencies
	);
	const additionalServicesTotal = sumServiceItemsByType(
		serviceItems,
		false,
		payCurrency,
		convertCurrency,
		pharmacyCurrency,
		allowedCurrencies
	);
	// Prefer tax/discount-adjusted section totals from baseSummary so Billing Summary
	// and grand total stay in sync when services discount/tax are applied.
	const servicesSectionTotal = toMoneyNumber(baseSummary.services_total ?? 0);
	const investigationTotal = toMoneyNumber(baseSummary.investigation_total ?? 0);
	// Package + services + lab (pharmacy stays outside; added into grand_total below).
	const packageSubTotal = toMoneyNumber(
		packagePrice + servicesSectionTotal + investigationTotal
	);

	return {
		...baseSummary,
		consultation_total: consultationTotal,
		additional_services_total: additionalServicesTotal,
		services_total: servicesSectionTotal,
		treatment_package_total: toMoneyNumber(packagePrice),
		investigation_total: investigationTotal,
		pharmacy_net_total: pharmacyNetTotal,
		package_sub_total: packageSubTotal,
		// Pay Remaining / API expect sub_total to include package amount, not services only.
		subtotal: packageSubTotal,
		sub_total: packageSubTotal,
		package_tax: toMoneyNumber(invoice.tax_amount || 0),
		package_net_total: packageSubTotal,
		grand_total: toMoneyNumber(packageSubTotal + pharmacyNetTotal),
		currency: payCurrency,
	};
};

export const buildTreatmentBillingPayloadExtras = ({
	prescription = {},
	serviceItems = [],
	pharmacyItems = [],
	summary = {},
	paymentInfo = {},
}) => {
	const invoice = prescription?.appointment_invoice || {};
	const consultation = prescription?.consultation || {};
	const pkgDetails = prescription?.package_payment_details || {};
	const packagePrice = getTreatmentPackagePrice(prescription);

	const treatmentPackageLine = {
		treatment_package_id:
			prescription.treatment_package_id ||
			invoice.patient_treatment_package_id ||
			invoice.treatment_package_id,
		package_name:
			consultation.package_name ||
			invoice.package_name ||
			pkgDetails.treatment_package?.package_name,
		package_price: packagePrice,
		no_of_sessions:
			consultation.no_of_sessions ||
			invoice.no_of_sessions ||
			pkgDetails.treatment_package?.no_of_sessions,
		pricing_model: pkgDetails.treatment_package?.pricing_model,
	};

	return {
		is_treatment_package: true,
		invoice_type: "treatment_package",
		treatment_package_id: prescription.treatment_package_id || invoice.patient_treatment_package_id,
		patient_treatment_package_id:
			invoice.patient_treatment_package_id || prescription.treatment_package_id,
		session_id: prescription.session_id,
		treatment_package: [treatmentPackageLine],
		treatment_package_details: {
			package_name: treatmentPackageLine.package_name,
			total_sessions: treatmentPackageLine.no_of_sessions,
			current_session: pkgDetails.current_session?.session_name,
			current_session_number: pkgDetails.current_session?.session_number,
			treatment_start_date: pkgDetails.treatment_package?.sessions?.[0]?.session_date_time,
			doctor_name: prescription.doctor?.name,
			session_id: prescription.session_id,
		},
		treatment_history: (pkgDetails.session_details || []).map((entry) => ({
			session_name: entry.session?.session_name,
			session_date: entry.session?.session_date_time,
			status: entry.session?.status,
			paid_amount: toMoneyNumber(entry.paid_amount),
			payment_mode: entry.payment_mode,
		})),
		package_payment_details: pkgDetails,
		appointment_invoice: invoice,
		summary: {
			...summary,
			consultation_total: summary.consultation_total ?? 0,
			services_total:
				summary.services_total ?? summary.consultation_total ?? 0,
			additional_services_total: summary.additional_services_total ?? 0,
			treatment_package_total: packagePrice,
			investigation_total: summary.investigation_total ?? 0,
			pharmacy_net_total: summary.pharmacy_net_total ?? 0,
			package_sub_total: summary.package_sub_total ?? 0,
			package_tax: summary.package_tax ?? 0,
			package_net_total: summary.package_net_total ?? 0,
			// Keep package+services(+lab) on both keys used by UI and pay-remaining API.
			sub_total: toMoneyNumber(
				summary.package_sub_total ??
					summary.sub_total ??
					summary.subtotal ??
					packagePrice
			),
			subtotal: toMoneyNumber(
				summary.package_sub_total ??
					summary.sub_total ??
					summary.subtotal ??
					packagePrice
			),
			grand_total: summary.grand_total ?? 0,
			currency: summary.currency || invoice.currency || "INR",
		},
		payment: paymentInfo,
	};
};
